/**
 * Objection Defense Scenario — 40 Characters
 *
 * Unlike Cold Call Mastery (getting past the first 30 seconds), Objection Defense
 * begins MID-CONVERSATION. The prospect has already heard your pitch and is
 * throwing objections. Your job is to handle their resistance and move forward.
 *
 * 5 sub-categories of objections:
 *   1. price-objection      — "Too expensive" / "Send me a discount"
 *   2. happy-with-vendor    — "We're locked in with our current provider"
 *   3. not-right-time       — "Maybe next quarter / next year"
 *   4. send-more-info       — "Just email me something"
 *   5. need-approval        — "I need to run this by my boss/team/legal"
 *
 * silenceProfile.stages[] controls hangup timing:
 *   - type: "warn" | "hangup"
 *   - delay: milliseconds of silence before triggering
 *   - lineKey: key in character.lines to speak
 *   - (audio is looked up by lineKey: see ../audio.js and audioManifest.json)
 */

export const objectionDefense = {

  // ═════════════════════════════════════════════════════════════════
  // 1. SARAH MITCHELL — The Sticker Shock CFO
  // ═════════════════════════════════════════════════════════════════
  'sarah-mitchell-cfo': {
    id: 'sarah-mitchell-cfo',
    name: 'Sarah Mitchell',
    role: 'CFO',
    company: 'BrightFlow Software',
    industry: 'SaaS',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/objection-defense/sarah-mitchell-cfo.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Sticker Shock CFO',
    scenarioDesc: "Sarah is 41, sharp, and just heard your quote. She nearly choked on her coffee. She's not necessarily a 'no' — she's just stunned. Her instinct is to push back hard on price out of pure reflex. She respects reps who don't immediately discount but who reframe value.",
    goal: 'Defend your price without discounting and get her to agree to a follow-up call.',
    category: 'objection-defense',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Hold on — eighteen thousand a year? That's three times what we pay now. There's no way I can justify that to the board.",
      impatient1: "Are you still there? Because right now I'm seeing a 200% price increase for no clear reason.",
      impatient2: "Either justify the number or knock 40% off. Otherwise we're done.",
      hangup: "Yeah, I can't make this work. Send the proposal but I'm not optimistic. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't apologize for the price. Reframe immediately around the cost of NOT having the solution. Quantify the pain.",
      commonMistake: "Caving instantly and offering a discount. She'll respect you less and you've trained her to push harder.",
      winningMove: "Say: 'Sarah, I hear you. But before we talk discount, how much is your team currently spending on manual reporting per week? That's the comparison that matters.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 2. ROBERTO ALVAREZ — The Loyal Lifer
  // ═════════════════════════════════════════════════════════════════
  'roberto-alvarez-operations-director': {
    id: 'roberto-alvarez-operations-director',
    name: 'Roberto Alvarez',
    role: 'Operations Director',
    company: 'CoastalLogix Supply',
    industry: 'logistics',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/roberto-alvarez-operations-director.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Loyal Lifer',
    scenarioDesc: "Roberto is 54, Mexican-American, and has used the same logistics vendor for 12 years. They take him to baseball games. His son interned there. Loyalty is personal for him. He's not hostile — he's protective. Bashing the incumbent is the fastest way to lose him.",
    goal: 'Get him to admit one limitation of his current vendor.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Look, I appreciate the pitch, but we've been with Garcia Freight for twelve years. They've never let us down. I'm not looking to switch.",
      impatient1: "You're not really hearing me. These guys are family to us.",
      impatient2: "Trying to bad-mouth them isn't going to work. They know our business inside and out.",
      hangup: "I think we're done here. Have a good one. *click*"
    },
    coachingNotes: {
      whatWorks: "Never attack the incumbent. Position yourself as complementary, not replacement. Ask about a SPECIFIC capability his vendor likely lacks.",
      commonMistake: "Saying 'but I bet they don't do X' — it sounds aggressive and disrespectful. He'll defend them harder.",
      winningMove: "Say: 'Roberto, I'd never ask you to leave a 12-year partnership. I'm curious though — when they handle your peak-season volume, are you 100% satisfied with their tracking visibility?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 3. KAREN BLAKE — The Maybe Next Year
  // ═════════════════════════════════════════════════════════════════
  'karen-blake-marketing-director': {
    id: 'karen-blake-marketing-director',
    name: 'Karen Blake',
    role: 'Marketing Director',
    company: 'Northvale Retail Group',
    industry: 'retail',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/karen-blake-marketing-director.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Maybe Next Year',
    scenarioDesc: "Karen is 48, polite, and uses 'timing' as her permanent excuse. It's not the right quarter. They're in the middle of a rebrand. Budget cycles. There's always something. She's used this dodge for years and it works on 90% of reps. The remaining 10% know how to crack it.",
    goal: 'Create urgency without being pushy and book a call within 2 weeks.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'warn', delay: 14000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 20000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Honestly, this sounds great, but we're just not ready right now. Maybe reach out again in Q3.",
      impatient1: "I really do think it's worth exploring — eventually. Just not now.",
      impatient2: "I appreciate the persistence, but pushing me harder won't change my timeline.",
      hangup: "Let's connect in the new year. Have a good day. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't accept the dodge politely. Ask SPECIFICALLY what changes by Q3. If she can't answer, the 'timing' excuse falls apart.",
      commonMistake: "Saying 'great, I'll follow up in Q3' and ending the call. She's just kicked you down the road forever.",
      winningMove: "Say: 'Karen, what specifically needs to change for Q3 to be the right time? Because if we wait three months you'll have lost roughly $80K in efficiency we could capture starting next week.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 4. DANIEL OKONKWO — The Email Black Hole
  // ═════════════════════════════════════════════════════════════════
  'daniel-okonkwo-it-manager': {
    id: 'daniel-okonkwo-it-manager',
    name: 'Daniel Okonkwo',
    role: 'IT Manager',
    company: 'Meridian Healthcare Partners',
    industry: 'healthcare',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/objection-defense/daniel-okonkwo-it-manager.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Email Black Hole',
    scenarioDesc: "Daniel is 37, Nigerian-American, and his strategy is to drown salespeople in 'send me an email.' He has 4,000 unread messages. Your email will join the graveyard. He's not malicious — he's just overwhelmed and uses email as a polite no.",
    goal: 'Refuse the email dodge and book a real 15-minute call.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 11000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "You know what, just send me an email with the details. I'll take a look when I get a chance.",
      impatient1: "I really do prefer email. I can review it when I have time.",
      impatient2: "I'm not going to commit to a call right now. Just email it.",
      hangup: "Email is the only way. Goodbye. *click*"
    },
    coachingNotes: {
      whatWorks: "Call out the email dodge gently. Get him to admit emails get buried. Then trade the email for a 15-minute calendar slot.",
      commonMistake: "Happily agreeing to send the email. You've just lost the deal — your email will sit unread for 6 months.",
      winningMove: "Say: 'Daniel, I'll be honest — I send these emails all the time and 90% never get read. Can we trade? I'll keep it to 15 minutes, you pick the day, and if it's not relevant I'll never bother you again.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 5. JESSICA ROMANO — The Approval Punter
  // ═════════════════════════════════════════════════════════════════
  'jessica-romano-purchasing-manager': {
    id: 'jessica-romano-purchasing-manager',
    name: 'Jessica Romano',
    role: 'Purchasing Manager',
    company: 'Greenpoint Manufacturing',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/jessica-romano-purchasing-manager.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Approval Punter',
    scenarioDesc: "Jessica is 33, Italian-American, and uses 'I need approval' as her shield. Sometimes she really does need approval. Sometimes she just doesn't want to decide. She's not the final decision maker but she's the gatekeeper. Reps who write her off lose the deal. Reps who arm her to sell upward win.",
    goal: 'Equip her with the materials to champion you internally.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I'm interested, but I'd need to run this by my director and then probably finance. So this isn't really a decision I can make.",
      impatient1: "I told you, it's not up to me alone. I need their buy-in.",
      impatient2: "Pushing me harder isn't going to bypass the approval process.",
      hangup: "I'll mention it to them. That's the best I can do. *click*"
    },
    coachingNotes: {
      whatWorks: "Treat her as your champion, not your obstacle. Ask what her director cares about. Build the pitch she'll deliver internally.",
      commonMistake: "Asking 'can you transfer me to the decision maker?' She'll feel disrespected and ghost you forever.",
      winningMove: "Say: 'Jessica, totally fair. Let me ask — when you take this to your director, what's the first question he'll ask? I want to make sure you have the ammunition.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 6. MICHAEL PATEL — The Discount Demander
  // ═════════════════════════════════════════════════════════════════
  'michael-patel-ceo': {
    id: 'michael-patel-ceo',
    name: 'Michael Patel',
    role: 'CEO',
    company: 'Velocity Labs',
    industry: 'SaaS',
    tier: 'startup',
    avatar: '/sales-negotiation/characters/objection-defense/michael-patel-ceo.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Discount Demander',
    scenarioDesc: "Michael is 36, Indian-American, runs a Series A startup and reflexively asks for 30% off everything. It's not personal — it's startup culture. He expects every vendor to negotiate. If you cave too fast he loses respect for you AND your product.",
    goal: 'Hold price without losing the deal.',
    category: 'objection-defense',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 4000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 9000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Yeah, we're interested. But we're a startup — what's the actual price after the startup discount? Because I'm not paying list.",
      impatient1: "Come on, every vendor we work with gives us at least 25% off. What can you do?",
      impatient2: "If you can't move on price, this isn't going to work. We're tight on burn.",
      hangup: "Get back to me when you can give me a real number. *click*"
    },
    coachingNotes: {
      whatWorks: "Trade, don't give. If he wants a discount, ask for something — annual commitment, case study rights, faster signing. Never discount for free.",
      commonMistake: "Either flat refusing or immediately offering 20% off. Both signal weakness.",
      winningMove: "Say: 'I can get you to 15% off — but I'd need an annual commitment and permission to use you as a case study. Fair trade?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 7. WILLIAM REYNOLDS — The Vendor Bigamist
  // ═════════════════════════════════════════════════════════════════
  'william-reynolds-cto': {
    id: 'william-reynolds-cto',
    name: 'William Reynolds',
    role: 'CTO',
    company: 'Pinnacle Analytics',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/william-reynolds-cto.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Vendor Bigamist',
    scenarioDesc: "William is 45, British, and has THREE tools that overlap with what you're selling. He's bought, abandoned, and rebought solutions in this space twice. He's deeply skeptical of switching because he's been burned. Migration trauma is real for him.",
    goal: 'Get him to admit a specific gap his current stack does not cover.',
    category: 'objection-defense',
    personalityType: 'skeptical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Honestly, we already have Tableau, Looker, and Segment. I don't see what you'd add. We've tried four of these tools already.",
      impatient1: "Look, every vendor says they're different. I've heard it before.",
      impatient2: "I've been burned twice migrating between platforms. The pain isn't worth it.",
      hangup: "We're set with what we have. Best of luck. *click*"
    },
    coachingNotes: {
      whatWorks: "Acknowledge the tool fatigue. Then ask a SPECIFIC technical question that exposes a real gap in his stack.",
      commonMistake: "Saying 'but we do X better.' He's heard it. Be specific about a use case his tools genuinely can't handle.",
      winningMove: "Say: 'I get the fatigue. Quick question — when your data team needs to attribute pipeline back to specific paid ad campaigns at the contact level, which of those three tools handles that today?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 8. AMINA YUSUF — The Tire-Kicker
  // ═════════════════════════════════════════════════════════════════
  'amina-yusuf-hr-director': {
    id: 'amina-yusuf-hr-director',
    name: 'Amina Yusuf',
    role: 'HR Director',
    company: 'BrightPath Consulting',
    industry: 'consulting',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/objection-defense/amina-yusuf-hr-director.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Information Collector',
    scenarioDesc: "Amina is 39, Somali-British, and her tactic is to ask for endless documentation. Whitepapers, ROI calculators, case studies, security reviews, demo videos. She'll never decide because she's always 'gathering information.' She's not malicious — she's risk-averse and confuses research with progress.",
    goal: 'Stop the document spiral and get a decision deadline.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'warn', delay: 14000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 20000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Can you send me your SOC 2 report, GDPR documentation, three case studies in our industry, ROI calculator, and a recorded demo? I'll need all of that before I can even consider this.",
      impatient1: "I really do need to review everything thoroughly before making a recommendation.",
      impatient2: "There's no decision until I've done my due diligence.",
      hangup: "I'll review what you've sent and get back to you. Eventually. *click*"
    },
    coachingNotes: {
      whatWorks: "Ask what decision the documents will enable. Force her to commit to a timeline. If you send everything with no commitment, you'll never hear back.",
      commonMistake: "Promising to send the whole package immediately. She'll vanish into the documentation cycle.",
      winningMove: "Say: 'Amina, I'll send everything you need. But before I do — what's the decision this will support, and by when? I want to make sure I send the right docs for that timeline.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 9. THOMAS WHITNEY — The Bargain Hunter
  // ═════════════════════════════════════════════════════════════════
  'thomas-whitney-hardware-store-owner': {
    id: 'thomas-whitney-hardware-store-owner',
    name: 'Thomas Whitney',
    role: 'Hardware Store Owner',
    company: "Whitney's Hardware",
    industry: 'retail',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/objection-defense/thomas-whitney-hardware-store-owner.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Bargain Hunter',
    scenarioDesc: "Thomas is 61, American, runs a small hardware store. He's haggled with vendors for 35 years. He'll never accept the first price. His whole identity is finding the deal. He won't be offended if you hold firm — he expects the dance.",
    goal: 'Hold close to list price while making him feel like he won the negotiation.',
    category: 'objection-defense',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Now look, I been doing this 35 years. Nobody pays sticker price. What's your real number?",
      impatient1: "Come on now, throw me a bone. Half off? Free shipping? Something.",
      impatient2: "Every other vendor sweetens the deal. You better start sweetening.",
      hangup: "No deal, no thanks. I'll find another. *click*"
    },
    coachingNotes: {
      whatWorks: "Give him a small win — extended trial, free training, a bundled add-on. He needs to feel like he hustled you, not that you discounted.",
      commonMistake: "Holding firm with no concession at all. He'll feel disrespected and walk.",
      winningMove: "Say: 'Tom, I can't move the price, but I can throw in 60 days of premium support on the house. That's a $400 value. Deal?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 10. PRIYA SHARMA — The Comparison Shopper
  // ═════════════════════════════════════════════════════════════════
  'priya-sharma-procurement-lead': {
    id: 'priya-sharma-procurement-lead',
    name: 'Priya Sharma',
    role: 'Procurement Lead',
    company: 'Acuity Financial Services',
    industry: 'finance',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/priya-sharma-procurement-lead.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Competitor Lever',
    scenarioDesc: "Priya is 38, Indian, and her job is to drive down prices by playing vendors against each other. She'll quote your competitor's price even if she made it up. She respects reps who don't panic. She loses respect for reps who match without verification.",
    goal: 'Refuse to match a fake price without losing the deal.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 11000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Your competitor quoted us $14,000 for essentially the same thing. Match it or we're going with them.",
      impatient1: "I don't have time to debate apples to apples. The number is $14K.",
      impatient2: "Final chance. Match the price or I'll sign with them tomorrow.",
      hangup: "Fine. Going with them. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't match blindly. Ask which product, which features, which terms. Most 'competitor quotes' fall apart under scrutiny.",
      commonMistake: "Either matching the price immediately or refusing rudely. Both lose the deal.",
      winningMove: "Say: 'Priya, I'd love to match — but $14K is below our floor for any package with our SLA. Can I see what's in their proposal? I'd bet there's a feature gap that matters to you.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 11. NATHAN COLE — The Q1 Budget Excuse
  // ═════════════════════════════════════════════════════════════════
  'nathan-cole-vp-finance': {
    id: 'nathan-cole-vp-finance',
    name: 'Nathan Cole',
    role: 'VP Finance',
    company: 'Stoneridge Industries',
    industry: 'manufacturing',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/nathan-cole-vp-finance.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Frozen Budget',
    scenarioDesc: "Nathan is 51, polished, and uses 'we're locked in for the fiscal year' as a permanent shield. The budget IS locked — but he also has $200K in discretionary spend he just doesn't want to mention. He's testing how hard you'll push.",
    goal: 'Find the discretionary budget without insulting him.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Our fiscal year budget is locked. I can't allocate new spend until July. Sorry.",
      impatient1: "I appreciate the pitch, but the budget is what it is.",
      impatient2: "I'm not going to magically find money. Talk to me next fiscal year.",
      hangup: "Talk in July. Have a good one. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't fight the budget freeze. Ask about discretionary, pilot, or experimental funds. Most VPs of Finance have $50K-$500K of unallocated money.",
      commonMistake: "Saying 'okay, I'll follow up in July.' He'll ghost you in July.",
      winningMove: "Say: 'Nathan, totally understood. Most companies your size have a discretionary or innovation budget that's separate. Is there any flexibility there for a pilot under $20K?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 12. ELENA VARGAS — The Rebuild Just Happened
  // ═════════════════════════════════════════════════════════════════
  'elena-vargas-it-director': {
    id: 'elena-vargas-it-director',
    name: 'Elena Vargas',
    role: 'IT Director',
    company: 'Coastline Hospitality Group',
    industry: 'hospitality',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/elena-vargas-it-director.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Recent Migration',
    scenarioDesc: "Elena is 44, Cuban-American, and just finished an 18-month, $400K migration to a new platform. She's exhausted. The last thing she wants is to consider another switch. She's not lying when she says no — she's traumatized.",
    goal: 'Get her to consider a complementary tool that does not require migration.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "We just finished an 18-month migration. Nobody — and I mean NOBODY — wants to think about another platform change. Don't even start.",
      impatient1: "I'm serious. My team would mutiny.",
      impatient2: "We are not doing another migration. End of conversation.",
      hangup: "Try us again in three years. Goodbye. *click*"
    },
    coachingNotes: {
      whatWorks: "Stop selling migration. Sell augmentation. Your tool sits ON TOP of her existing stack without replacing anything.",
      commonMistake: "Pitching the rip-and-replace value. She'll hang up before you finish the sentence.",
      winningMove: "Say: 'Elena, I'd never ask you to migrate. We sit on top of your existing platform via API — zero changes to what your team uses every day. Want to see how that works?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 13. JAMES O'CONNOR — The Internal Champion Needed
  // ═════════════════════════════════════════════════════════════════
  'james-oconnor-product-manager': {
    id: 'james-oconnor-product-manager',
    name: "James O'Connor",
    role: 'Product Manager',
    company: 'Atlas Insurance Group',
    industry: 'insurance',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/james-oconnor-product-manager.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Buy-In Builder',
    scenarioDesc: "James is 41, Irish-American, and likes what you're selling but can't get internal alignment. Three stakeholders, three different priorities. He needs help building consensus. Most reps abandon him because the deal feels too complicated. The reps who help him win.",
    goal: 'Build a multi-stakeholder rollout plan he can sell internally.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I want this. But our CIO wants security, our CFO wants ROI, and our COO wants no operational disruption. I cannot get them all aligned.",
      impatient1: "You're not solving the actual problem. The problem isn't your product. It's internal politics.",
      impatient2: "I need a way to get three executives to agree. That's the real challenge.",
      hangup: "I'll have to figure this out on my own. *click*"
    },
    coachingNotes: {
      whatWorks: "Offer to build a 1-page summary for EACH stakeholder, addressing their specific concern. Become his weapon, not his burden.",
      commonMistake: "Sending one generic deck. He'll fail to align his team and the deal dies.",
      winningMove: "Say: 'James, let me build you three things — a security memo for the CIO, an ROI page for the CFO, an implementation plan for the COO. Same product, three angles. Then they can each say yes for their own reason.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 14. RACHEL DUNN — The Endless Discount Loop
  // ═════════════════════════════════════════════════════════════════
  'rachel-dunn-controller': {
    id: 'rachel-dunn-controller',
    name: 'Rachel Dunn',
    role: 'Controller',
    company: 'Westbrook Logistics',
    industry: 'logistics',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/rachel-dunn-controller.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Endless Negotiator',
    scenarioDesc: "Rachel is 47, will accept any discount you offer, and then immediately ask for more. She'll go from 'I need 10% off' to 'I need 25% off' in two minutes. She's testing your floor. Reps who keep caving get used until there's nothing left.",
    goal: 'Stop the slide and lock in a number she will sign at.',
    category: 'objection-defense',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 11000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Okay, 10% off works. But honestly, I was thinking more like 20%. Can you stretch?",
      impatient1: "And actually, can we throw in two more seats while we're at it?",
      impatient2: "One more ask — can you waive the implementation fee too?",
      hangup: "If this is your best, I'll need to think on it. *click*"
    },
    coachingNotes: {
      whatWorks: "Anchor a final number explicitly. Say 'this is my last move' and mean it. If you keep moving she'll keep asking.",
      commonMistake: "Keep saying 'okay, I can do that' until your margin is zero.",
      winningMove: "Say: 'Rachel, here's my last move — 15% off, two extra seats included, but the implementation fee stays. This is it. Yes or no today?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 15. KEVIN MURPHY — The Procurement Stall
  // ═════════════════════════════════════════════════════════════════
  'kevin-murphy-director-of-procurement': {
    id: 'kevin-murphy-director-of-procurement',
    name: 'Kevin Murphy',
    role: 'Director of Procurement',
    company: 'Granite State Hospital Network',
    industry: 'healthcare',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/kevin-murphy-director-of-procurement.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Process Gauntlet',
    scenarioDesc: "Kevin is 53, Irish-American, and his entire job is creating friction. RFPs, security reviews, legal review, sign-off committees. He's not against you — he's just paid to slow everything down. Reps who try to bypass him get blocked permanently.",
    goal: 'Get him on your side and accelerate the procurement timeline.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'warn', delay: 14000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 20000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Our procurement process takes 6 months. RFP, security review, legal, finance committee, board approval. That's how we do things.",
      impatient1: "I don't make exceptions. The process exists for a reason.",
      impatient2: "If you're trying to bypass procurement, this deal is dead before it starts.",
      hangup: "Submit through the standard channel. Goodbye. *click*"
    },
    coachingNotes: {
      whatWorks: "Respect the process publicly. Then ask if there's an accelerated track for vendors that meet pre-screening criteria.",
      commonMistake: "Trying to skip procurement. Kevin will torpedo your deal personally.",
      winningMove: "Say: 'Kevin, I have huge respect for procurement. Is there an accelerated path for vendors who are already SOC 2 certified and HIPAA-compliant? I want to play by your rules, just on the fastest version of them.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 16. SOPHIE LARSSON — The Pilot-Forever
  // ═════════════════════════════════════════════════════════════════
  'sophie-larsson-head-of-innovation': {
    id: 'sophie-larsson-head-of-innovation',
    name: 'Sophie Larsson',
    role: 'Head of Innovation',
    company: 'Nordic Energy AB',
    industry: 'energy',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/sophie-larsson-head-of-innovation.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Eternal Pilot',
    scenarioDesc: "Sophie is 40, Swedish, and runs 'innovation pilots' that never become real contracts. She'll happily pay you $10K for a 3-month pilot and then quietly let it expire. Your job is to either kill the pilot trap or build an exit clause that turns the pilot into a contract.",
    goal: 'Convert a pilot ask into a real signed deal with milestones.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "We love testing new tools. Can we do a 3-month pilot for, say, $10K? Then we'll evaluate.",
      impatient1: "The pilot is our standard approach. Take it or leave it.",
      impatient2: "If you can't pilot, we can't proceed.",
      hangup: "We only do pilots. Best of luck elsewhere. *click*"
    },
    coachingNotes: {
      whatWorks: "Pilots without success criteria are graveyards. Define what 'success' looks like and tie a contract clause to it. Auto-convert on pilot success.",
      commonMistake: "Agreeing to a vague pilot. You'll do all the work, get all the data, and watch the pilot die quietly.",
      winningMove: "Say: 'Sophie, I love pilots — but only ones with teeth. Let's define three success metrics. If we hit all three at month 3, we auto-convert to a 12-month contract. If we miss, we part ways. Fair?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 17. BENJAMIN HARTLEY — The Spousal Veto
  // ═════════════════════════════════════════════════════════════════
  'benjamin-hartley-restaurant-owner': {
    id: 'benjamin-hartley-restaurant-owner',
    name: 'Benjamin Hartley',
    role: 'Restaurant Owner',
    company: "Hartley's Tavern",
    industry: 'hospitality',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/objection-defense/benjamin-hartley-restaurant-owner.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Spousal Veto',
    scenarioDesc: "Benjamin is 49, owns a restaurant with his wife Diane. He likes what you're selling but uses 'I need to ask my wife' as a permanent shield. Sometimes it's real. Sometimes it's a polite no. Either way, you can't close without addressing Diane.",
    goal: 'Get him to set up a 3-way call with his wife.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Look, I like the idea, but I'd need to run this by my wife. We own the place together.",
      impatient1: "She handles the financial side. I can't sign anything without her.",
      impatient2: "She's the brake. If she says no, it's no.",
      hangup: "I'll talk to her and get back to you. Eventually. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't try to override Diane. Make her part of the conversation. Offer a quick 3-way call so you can address her concerns directly.",
      commonMistake: "Saying 'sure, talk to her and let me know.' He'll never call back because Diane has heard nothing about you and will default to no.",
      winningMove: "Say: 'Benjamin, totally fair — and honestly, if Diane handles the finances, she should hear this from me directly. Can we do a 15-minute call with all three of us this week?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 18. ALEXANDRA CHEN — The Already Heard This
  // ═════════════════════════════════════════════════════════════════
  'alexandra-chen-cmo': {
    id: 'alexandra-chen-cmo',
    name: 'Alexandra Chen',
    role: 'CMO',
    company: 'Vivid Commerce',
    industry: 'retail',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/alexandra-chen-cmo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Pitch-Fatigued Buyer',
    scenarioDesc: "Alexandra is 39, Chinese-American, and has heard 47 versions of your pitch this year. She can finish your sentences. She's not hostile — she's exhausted. To break through you need something genuinely novel or a fresh angle.",
    goal: 'Disrupt her pattern recognition and earn 10 minutes of real attention.',
    category: 'objection-defense',
    personalityType: 'skeptical',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Let me guess — you'll save us money, increase efficiency, and give us better data. Did I miss anything?",
      impatient1: "I've heard this exact pitch eleven times this quarter. Tell me something new or we're done.",
      impatient2: "You're confirming my suspicion that you sound just like everyone else.",
      hangup: "Same pitch, different rep. Bye. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't sell features. Lead with a specific, contrarian insight about her industry that she can't dismiss with 'heard that before.'",
      commonMistake: "Continuing the standard pitch. She's pattern-matching you to the 47 reps before you. Be different.",
      winningMove: "Say: 'You're right, you've heard it. So let me skip the pitch entirely — here's the one thing your competitors are doing this quarter that you're probably not: [specific tactic]. Want me to walk you through it?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 19. MARCUS THORN — The ROI Skeptic
  // ═════════════════════════════════════════════════════════════════
  'marcus-thorn-coo': {
    id: 'marcus-thorn-coo',
    name: 'Marcus Thorn',
    role: 'COO',
    company: 'BlueRidge Manufacturing',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/marcus-thorn-coo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The ROI Demolisher',
    scenarioDesc: "Marcus is 56, blunt, and will tear apart your ROI calculation in real time. He has a PhD in industrial engineering. He's been pitched 'save 30%' claims his entire career and 80% of them turned out to be wrong. He respects honest math and despises inflated numbers.",
    goal: 'Defend your ROI with conservative numbers he can verify.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Your slide claims 32% efficiency gain. That's a fantasy number. Show me the math or stop wasting my time.",
      impatient1: "Where's the methodology? Where's the sample size? Which industries?",
      impatient2: "I can poke six holes in your ROI calculation already. Try again.",
      hangup: "Bring me real numbers or don't bother. *click*"
    },
    coachingNotes: {
      whatWorks: "Use the most conservative number you can defend. Show the methodology, the sample size, the customer list. Let him pick apart real data, not marketing claims.",
      commonMistake: "Defending the 32% claim. Drop it. Replace with a verifiable lower number with full transparency.",
      winningMove: "Say: 'Marcus, you're right to push back. Forget the 32%. My conservative floor across 8 manufacturing clients is 11% with a 95% confidence interval. Want to see the dataset?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 20. NOOR ABDULLAH — The Soft No
  // ═════════════════════════════════════════════════════════════════
  'noor-abdullah-dean-of-students': {
    id: 'noor-abdullah-dean-of-students',
    name: 'Noor Abdullah',
    role: 'Dean of Students',
    company: 'Eastlake Community College',
    industry: 'education',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/noor-abdullah-dean-of-students.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Polite Vanisher',
    scenarioDesc: "Noor is 45, Lebanese-American, and her objection style is to be SO polite you can't tell she's saying no. She'll say 'this is wonderful' and 'I love the idea' and then never call back. The hardest objection to handle: she won't tell you it's a no.",
    goal: 'Surface the real objection she is hiding behind politeness.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 8000, lineKey: 'impatient1' },
        { type: 'warn', delay: 15000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 22000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Oh this is wonderful! What a great idea. I love what you're doing. Let me think about it.",
      impatient1: "I really do love it. I just need some time to consider everything.",
      impatient2: "I appreciate your enthusiasm so much. I'll be in touch when the time is right.",
      hangup: "Thank you so much for calling. Have a beautiful day. *click*"
    },
    coachingNotes: {
      whatWorks: "Ask directly for a no. 'Permission to be turned down' disarms her politeness and forces honesty.",
      commonMistake: "Believing her enthusiasm. She's never going to call back. The 'wonderful' is a brushoff disguised as a compliment.",
      winningMove: "Say: 'Noor, I really appreciate that. Can I ask you something direct? If you knew right now this wasn't going to be a fit, would you tell me? Because I'd rather a clean no today than chase a maybe for six months.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 21. CHRISTOPHER ADAMS — The Legal Block
  // ═════════════════════════════════════════════════════════════════
  'christopher-adams-general-counsel': {
    id: 'christopher-adams-general-counsel',
    name: 'Christopher Adams',
    role: 'General Counsel',
    company: 'Sterling Financial',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/christopher-adams-general-counsel.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Legal Wall',
    scenarioDesc: "Christopher is 48, the GC, and his entire job is saying no to risk. Your contract terms scare him. Your security claims scare him. Your data handling scares him. He's not the buyer but he can kill any deal.",
    goal: 'Get him to commit to a specific list of concerns rather than blanket rejection.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I've reviewed your contract. There are too many concerns to list. We can't sign this.",
      impatient1: "I don't have time to walk you through every issue.",
      impatient2: "If your standard contract is unacceptable, we shouldn't be doing business.",
      hangup: "The contract is the contract. Not signing. Goodbye. *click*"
    },
    coachingNotes: {
      whatWorks: "Force him to name specific clauses. 'Too many concerns' is unactionable. Get a numbered redline list.",
      commonMistake: "Defending the whole contract. You're playing his game on his terms. Make him specify.",
      winningMove: "Say: 'Christopher, I can't fix what I can't see. Can you give me your top 3 specific clauses to redline? If we can resolve those, can we move forward?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 22. ANGELA WRIGHT — The Already Paid For It
  // ═════════════════════════════════════════════════════════════════
  'angela-wright-vp-marketing': {
    id: 'angela-wright-vp-marketing',
    name: 'Angela Wright',
    role: 'VP Marketing',
    company: 'Hartwell Direct',
    industry: 'marketing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/angela-wright-vp-marketing.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Sunk Cost Defender',
    scenarioDesc: "Angela is 50, just signed a 3-year contract with your competitor 6 months ago. She defended the choice publicly. Switching now would mean admitting she was wrong. Sunk cost AND ego trap. She's not going to switch — but she might add a complementary tool.",
    goal: 'Pivot from replacement to addition.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I just signed a three-year deal with your competitor in March. I'm locked in. There's nothing to talk about.",
      impatient1: "Switching now would be a disaster — financially and politically.",
      impatient2: "I made my call. I'm not unmaking it.",
      hangup: "Talk in two and a half years. Bye. *click*"
    },
    coachingNotes: {
      whatWorks: "Stop selling the switch. Sell the gap. Position yourself as filling a hole their tool doesn't address.",
      commonMistake: "Trying to talk her out of her last decision. She'll dig in to defend herself.",
      winningMove: "Say: 'Angela, I'd never suggest switching from a tool you just signed for. But there's one specific use case that platform doesn't handle — what if I could plug that gap without you touching the existing contract?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 23. DEVIN PARK — The Free Trial Forever
  // ═════════════════════════════════════════════════════════════════
  'devin-park-founder': {
    id: 'devin-park-founder',
    name: 'Devin Park',
    role: 'Founder',
    company: 'TideRunner Apps',
    industry: 'SaaS',
    tier: 'startup',
    avatar: '/sales-negotiation/characters/objection-defense/devin-park-founder.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Trial Extender',
    scenarioDesc: "Devin is 28, Korean-American, and exists on a permanent free trial. He's been on your 14-day trial for 87 days. He keeps asking for extensions. He's not going to convert without pressure. Cutting him off is the only path to a sale.",
    goal: 'End the free trial and force a conversion decision.',
    category: 'objection-defense',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 11000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Hey so the trial's been awesome. Can I get another 30 days extension? Just need a bit more time to evaluate.",
      impatient1: "Come on, I've been a great user. Just one more month.",
      impatient2: "If you cut me off I'll have to find another solution. Don't make me do that.",
      hangup: "Then we'll churn. Your call. *click*"
    },
    coachingNotes: {
      whatWorks: "Make the cost of NOT converting more painful than converting. Cut the trial off. He'll either pay or churn — both better than perpetual free use.",
      commonMistake: "Extending the trial 'just one more time.' You're training him that free is free forever.",
      winningMove: "Say: 'Devin, I love that you're getting value. But we can't keep extending. The trial ends Friday. We have a startup tier at $99/mo if budget is the issue. Yes or no by Friday.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 24. MARIA GONZALEZ — The Reorg Excuse
  // ═════════════════════════════════════════════════════════════════
  'maria-gonzalez-vp-of-people': {
    id: 'maria-gonzalez-vp-of-people',
    name: 'Maria Gonzalez',
    role: 'VP of People',
    company: 'Crestwood Health',
    industry: 'healthcare',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/maria-gonzalez-vp-of-people.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Forever Reorg',
    scenarioDesc: "Maria is 47, Mexican-American, and her company is ALWAYS in the middle of a reorganization. Every quarter there's a new structure, new reporting line, new initiative. She uses this perpetual chaos to dodge decisions. The reorg is real — but it's also her shield.",
    goal: 'Find the stable team or decision-maker the reorg does not touch.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Honestly, we're in the middle of a massive reorganization. I don't even know what my team will look like in 90 days. Now's a bad time.",
      impatient1: "Everything is in flux. I can't commit to anything until the dust settles.",
      impatient2: "You're going to get the same answer in 90 days. We'll still be reorganizing.",
      hangup: "Catch me when things stabilize. If they ever do. *click*"
    },
    coachingNotes: {
      whatWorks: "Ask what team or function is NOT being reorganized. Find the stable corner of the business and start there.",
      commonMistake: "Waiting 90 days. The reorg will never end. You'll be back where you started.",
      winningMove: "Say: 'Maria, totally fair. Which part of your org is least affected by the restructure? Because that's where I'd want to start a small pilot — somewhere stable that won't blow up mid-project.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 25. JAMAL THOMPSON — The Built-It-In-House
  // ═════════════════════════════════════════════════════════════════
  'jamal-thompson-vp-engineering': {
    id: 'jamal-thompson-vp-engineering',
    name: 'Jamal Thompson',
    role: 'VP Engineering',
    company: 'Realtime Logistics',
    industry: 'logistics',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/jamal-thompson-vp-engineering.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Build vs Buy',
    scenarioDesc: "Jamal is 42, African-American, and his team built an in-house version of what you're selling. They've spent 18 months on it. They're 60% done. He's emotionally invested. He thinks they'll finish in 'three more months.' They won't.",
    goal: 'Get him to admit the in-house build is taking longer than expected.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "My team has been building this exact thing for 18 months. We're three months from launch. Why would we buy yours?",
      impatient1: "We're almost done. It would be insane to throw away 18 months of work.",
      impatient2: "You don't understand — we know our domain better than any vendor.",
      hangup: "We're building. End of story. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't attack the build. Ask about the TCO including maintenance, ongoing dev, and feature roadmap. Build is never 'done.'",
      commonMistake: "Trying to convince him to throw away the work. He'll defend the sunk cost.",
      winningMove: "Say: 'Jamal, I'd never tell you to throw away 18 months. But after launch, what's your ongoing maintenance cost? Two devs full-time? That's $400K a year forever. Have you factored that into the build vs buy math?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 26. EMILY KOSTA — The Decision by Committee
  // ═════════════════════════════════════════════════════════════════
  'emily-kosta-operations-director': {
    id: 'emily-kosta-operations-director',
    name: 'Emily Kosta',
    role: 'Operations Director',
    company: 'Northgate Educational Group',
    industry: 'education',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/emily-kosta-operations-director.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Consensus Trap',
    scenarioDesc: "Emily is 44, Greek-American, and her company decides everything by committee. Six people have to agree. They never all agree at the same time. Decisions take 8-12 months. She's not lying — but she also uses consensus as a permanent stall.",
    goal: 'Identify the one committee member who can champion the deal.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Every decision goes through a committee of six. We'd need consensus. Last vendor took us 11 months to approve.",
      impatient1: "I can't bypass committee. It would be career suicide.",
      impatient2: "Trying to rush us through the process will get you rejected on principle.",
      hangup: "Submit through the proper channels. *click*"
    },
    coachingNotes: {
      whatWorks: "Find the most enthusiastic committee member and make them your internal advocate. One champion can drive consensus faster than you can.",
      commonMistake: "Trying to pitch all six. You'll get water-down feedback from all of them.",
      winningMove: "Say: 'Emily, in your committee, who would gain the most from this? That's the person I want to spend 30 minutes with — not to skip the committee, but to give them the conviction to advocate inside it.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 27. RYAN MORGAN — The Performance Doubter
  // ═════════════════════════════════════════════════════════════════
  'ryan-morgan-director-of-data': {
    id: 'ryan-morgan-director-of-data',
    name: 'Ryan Morgan',
    role: 'Director of Data',
    company: 'Apex Insurance',
    industry: 'insurance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/ryan-morgan-director-of-data.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Benchmark Skeptic',
    scenarioDesc: "Ryan is 46, the data director, and your performance claims sound too good. He'll demand benchmarks from comparable companies, not the cherry-picked case studies. He's seen vendors inflate numbers and he's bitter about it. He respects radical transparency.",
    goal: 'Provide transparent benchmarks that survive his scrutiny.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Don't give me your top 5 case studies. Give me the MEDIAN result across all your customers. The honest number.",
      impatient1: "I want the bottom quartile too. What does the worst-performing client look like?",
      impatient2: "If you can't show me the full distribution, you're hiding bad outcomes.",
      hangup: "Sales theater. Not interested. *click*"
    },
    coachingNotes: {
      whatWorks: "Show the median AND the bottom quartile. Radical transparency disarms his cynicism. He'll trust you for showing the bad with the good.",
      commonMistake: "Sticking to the highlight reel. He's specifically asking for the average and you're showing the top 1%.",
      winningMove: "Say: 'Ryan, fair. Median customer sees 14% improvement. Bottom quartile sees 3-5%. Worst case in our base saw zero gain but no loss either. Want the full dataset?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 28. SAMANTHA REED — The Forever Discounting
  // ═════════════════════════════════════════════════════════════════
  'samantha-reed-cfo': {
    id: 'samantha-reed-cfo',
    name: 'Samantha Reed',
    role: 'CFO',
    company: 'Brightleaf Retail Holdings',
    industry: 'retail',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/samantha-reed-cfo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Volume Threat',
    scenarioDesc: "Samantha is 49, sharp, and her play is volume threats. 'Give me a deeper discount and we'll add seats later.' The 'later' never comes. She'll get the discount, the volume stays static, and your margin is gone forever. Reps fall for this constantly.",
    goal: 'Tie any discount to actual volume commitments with penalties.',
    category: 'objection-defense',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Give us volume pricing now and we'll bring you 200 more seats next year. Trust me.",
      impatient1: "We've done this before with other vendors. It always works out.",
      impatient2: "Don't be paranoid. Just give me the discount and we'll grow into it.",
      hangup: "If you don't trust us, this isn't going to work. *click*"
    },
    coachingNotes: {
      whatWorks: "Trade discount for contractual volume commitment with clawback clauses. If they don't add the seats, the price reverts.",
      commonMistake: "Accepting the verbal promise of future volume. You'll be eating that discount with no growth.",
      winningMove: "Say: 'Samantha, I'll give you volume pricing — contingent on a 200-seat addition within 12 months in writing. If you miss the target, we true-up to standard pricing retroactively. Fair?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 29. DAVID CHEN — The Reference Demand
  // ═════════════════════════════════════════════════════════════════
  'david-chen-vp-of-engineering': {
    id: 'david-chen-vp-of-engineering',
    name: 'David Chen',
    role: 'VP of Engineering',
    company: 'Voltage Systems',
    industry: 'manufacturing',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/david-chen-vp-of-engineering.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Reference Caller',
    scenarioDesc: "David is 50, Taiwanese-American, and demands 5 customer references — all in his exact industry, exact company size, exact use case. They have to take his call within a week. If you can't deliver references that match, he assumes you don't have real customers.",
    goal: 'Provide references that genuinely match without overpromising.',
    category: 'objection-defense',
    personalityType: 'skeptical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I need 5 references. Same industry, same revenue range, same use case. They need to take my call within a week.",
      impatient1: "Adjacent industry doesn't count. I want exact matches or nothing.",
      impatient2: "Five. Exact. Or we don't move forward.",
      hangup: "No references, no deal. *click*"
    },
    coachingNotes: {
      whatWorks: "Negotiate the reference criteria. 5 perfect matches may not exist. Trade for a smaller number plus a peer group analysis.",
      commonMistake: "Promising 5 references you can't deliver, then scrambling. You'll burn customers and lose the deal.",
      winningMove: "Say: 'David, 5 exact matches in your space is hard — we have 3 close fits I can confirm by Friday. Plus a peer group call with 4 similar manufacturing leaders. Would 3 plus 4 work?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 30. ANNA PETROVA — The Pricing Page Doubter
  // ═════════════════════════════════════════════════════════════════
  'anna-petrova-marketing-manager': {
    id: 'anna-petrova-marketing-manager',
    name: 'Anna Petrova',
    role: 'Marketing Manager',
    company: 'Crystal Goods',
    industry: 'retail',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/objection-defense/anna-petrova-marketing-manager.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Published Price',
    scenarioDesc: "Anna is 33, Russian-American, and she's seen your pricing page. She knows your published prices. She wants the price below the public number. She uses the public pricing as a ceiling to negotiate down from. Most reps panic when called out on public pricing.",
    goal: 'Hold the published price while making her feel respected.',
    category: 'objection-defense',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Your website says $999/month. Nobody pays sticker. What's the real price?",
      impatient1: "Don't insult me by quoting the website price. What's the discount?",
      impatient2: "If $999 is your real number, I'll go elsewhere.",
      hangup: "I'll find a vendor who values their customers. *click*"
    },
    coachingNotes: {
      whatWorks: "Hold the price but give a non-monetary concession. Pricing integrity matters — if you discount publicly visible prices, you lose all credibility.",
      commonMistake: "Cutting the price. Now your pricing page is a lie and word will spread.",
      winningMove: "Say: 'Anna, $999 is our real number — but I can include onboarding for free, which we normally charge $1,200 for. That's the most valuable thing I can give you without breaking our published pricing.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 31. JONATHAN ELLIS — The Switch Cost Math
  // ═════════════════════════════════════════════════════════════════
  'jonathan-ellis-chief-systems-architect': {
    id: 'jonathan-ellis-chief-systems-architect',
    name: 'Jonathan Ellis',
    role: 'Chief Systems Architect',
    company: 'Brookline Insurance',
    industry: 'insurance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/jonathan-ellis-chief-systems-architect.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Migration Calculator',
    scenarioDesc: "Jonathan is 53, has a spreadsheet calculating every cost of switching from his current vendor to you. Total: $850K including downtime, retraining, lost productivity. Your annual savings: $200K. The math says no. You need to attack his assumptions.",
    goal: 'Find an error or unaccounted benefit in his switch-cost model.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 8000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 15000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I've built a switch-cost spreadsheet. Total cost to migrate to you: $850K. Annual savings: $200K. Payback: over 4 years. The math is bad.",
      impatient1: "My numbers are conservative. The pain is real.",
      impatient2: "You need to find $650K of value I'm not seeing or this is dead.",
      hangup: "The math doesn't work. Bye. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't argue with the spreadsheet. Ask if it includes upside cases — risk reduction, new revenue from features his current vendor lacks, lifetime savings beyond year 4.",
      commonMistake: "Disputing his cost assumptions. He'll defend them line by line. Find what's missing instead.",
      winningMove: "Say: 'Jonathan, your cost side is solid. But does your model include the upside — new revenue from features your current tool can't deliver, or risk reduction from our SOC 2? Those are usually 30-40% of the real value.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 32. CYNTHIA OWENS — The Multi-Vendor Confusion
  // ═════════════════════════════════════════════════════════════════
  'cynthia-owens-procurement-lead': {
    id: 'cynthia-owens-procurement-lead',
    name: 'Cynthia Owens',
    role: 'Procurement Lead',
    company: 'Hartman Industries',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/cynthia-owens-procurement-lead.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The RFP Bake-Off',
    scenarioDesc: "Cynthia is 51, runs an RFP with 4 vendors. Everyone gets the same 80 questions. Whoever scores highest wins. She doesn't care about relationships. She cares about the spreadsheet. You need to win the spreadsheet AND make sure the right criteria are weighted heavily.",
    goal: 'Influence the scoring criteria toward your strengths.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "You're one of four vendors in our RFP. Answer the 80 questions and we'll score. Best score wins.",
      impatient1: "I'm not interested in 'differentiation conversations.' Just fill out the RFP.",
      impatient2: "Trying to influence the criteria mid-process is not allowed.",
      hangup: "Submit the RFP or withdraw. *click*"
    },
    coachingNotes: {
      whatWorks: "Politely ask if you can offer feedback on the RFP criteria BEFORE the deadline. Most procurement leads will allow this. Use it to highlight your unique strengths.",
      commonMistake: "Just filling out the RFP and hoping. The criteria are designed for an incumbent or another vendor. You need to influence the scoring.",
      winningMove: "Say: 'Cynthia, I'll absolutely fill out the RFP. Before I do, would you consider feedback on whether the scoring criteria fully reflect the use cases that matter most? I want to make sure we're being evaluated on what you actually need.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 33. MARTIN GRAY — The We Already Tried That
  // ═════════════════════════════════════════════════════════════════
  'martin-gray-coo': {
    id: 'martin-gray-coo',
    name: 'Martin Gray',
    role: 'COO',
    company: 'Whitfield Services',
    industry: 'services',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/martin-gray-coo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Burned-Before',
    scenarioDesc: "Martin is 58, tried a tool exactly like yours in 2019. It failed. The implementation was a disaster. The vendor went bankrupt. He's scarred. He's not going to take a chance on you no matter how different you claim to be. He needs proof you're not them.",
    goal: 'Separate your offering from the previous failed vendor.',
    category: 'objection-defense',
    personalityType: 'skeptical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "We tried this exact thing in 2019. It was a complete disaster. Vendor went under. We lost $600K and 9 months. I'm done with this category.",
      impatient1: "You're going to tell me you're different. They all say that.",
      impatient2: "I don't care how different you are. I care that I'm not going to be burned again.",
      hangup: "Burned once, never again. *click*"
    },
    coachingNotes: {
      whatWorks: "Acknowledge the wound. Don't dismiss it. Then ask specifically what went wrong in 2019 and show how your structure prevents it.",
      commonMistake: "Saying 'we're different.' Every failed vendor said that. You need specifics.",
      winningMove: "Say: 'Martin, what specifically broke in 2019? Was it implementation, support, the product, or the company stability? Because I want to address THAT failure, not the generic version.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 34. LISA PETERSON — The Status Quo Defender
  // ═════════════════════════════════════════════════════════════════
  'lisa-peterson-vp-strategy': {
    id: 'lisa-peterson-vp-strategy',
    name: 'Lisa Peterson',
    role: 'VP Strategy',
    company: 'Bluetide Financial',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/lisa-peterson-vp-strategy.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Comfortable Plateau',
    scenarioDesc: "Lisa is 49, her department is hitting all its targets. Things are fine. Not great, not bad. Fine. She has no burning platform. Why would she risk a major change for incremental upside? She needs to feel the cost of doing nothing more than the cost of doing something.",
    goal: 'Manufacture genuine urgency without inventing fake fears.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Things are fine. We're hitting our numbers. Why would I introduce risk and disruption for a 5% improvement?",
      impatient1: "I'm not looking for transformation. I'm looking for steady-state.",
      impatient2: "Manufactured urgency doesn't work on me. Things are genuinely fine.",
      hangup: "If 'fine' isn't enough for you, that's your problem. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't manufacture fear. Show what her competitors are doing. The cost of 'fine' is being overtaken by competitors who chose 'better.'",
      commonMistake: "Trying to scare her about industry disruption. She's seen the headlines. They don't move her.",
      winningMove: "Say: 'Lisa, fine is fine. But three of your closest competitors implemented this in the last 18 months. In two years, fine will be behind. Want to see what they're building?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 35. ANTHONY RUSSO — The Bundled Wars
  // ═════════════════════════════════════════════════════════════════
  'anthony-russo-enterprise-architect': {
    id: 'anthony-russo-enterprise-architect',
    name: 'Anthony Russo',
    role: 'Enterprise Architect',
    company: 'Pinnacle Financial Services',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/anthony-russo-enterprise-architect.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Microsoft Bundle Argument',
    scenarioDesc: "Anthony is 51, Italian-American, and his answer to every standalone vendor is 'isn't this in our Microsoft/Salesforce/Oracle bundle?' His CIO is pushing consolidation. Standalone tools are getting cut. You need to show why a specialist beats a generalist.",
    goal: 'Prove specialist value over the bundled generalist.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Isn't this basically already in our Microsoft 365 enterprise bundle? Why pay extra for a standalone?",
      impatient1: "My CIO is consolidating vendors. Standalone tools are getting cut.",
      impatient2: "If Microsoft does 80% of what you do, why pay for 100%?",
      hangup: "Going with the bundle. Sorry. *click*"
    },
    coachingNotes: {
      whatWorks: "Concede the 80%. Then prove the 20% that matters generates more value than the cost of standalone. Specifics, not vague claims.",
      commonMistake: "Arguing Microsoft is bad. He uses Microsoft for everything. Don't insult his platform.",
      winningMove: "Say: 'Anthony, you're right — Microsoft covers about 80% of the use case. But the 20% they don't do is exactly where your team is currently spending 200 manual hours a month. That's $180K of labor. We cost $30K.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 36. VICTORIA STONE — The CFO Veto
  // ═════════════════════════════════════════════════════════════════
  'victoria-stone-business-unit-lead': {
    id: 'victoria-stone-business-unit-lead',
    name: 'Victoria Stone',
    role: 'Business Unit Lead',
    company: 'Helios Energy',
    industry: 'energy',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/victoria-stone-business-unit-lead.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The CFO Killed It',
    scenarioDesc: "Victoria is 46, loved your product, championed it internally for 4 months, then her CFO killed the deal in a 10-minute review. She's furious but powerless. The CFO never spoke to you. He killed it based on a single slide. You need to get to him.",
    goal: 'Get access to the CFO directly to revive the deal.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I'm sorry. My CFO killed it. I championed this for 4 months and he killed it in 10 minutes. I'm done fighting this fight.",
      impatient1: "I can't go back to him. I've already used my political capital.",
      impatient2: "Going around me to him will make this worse, not better.",
      hangup: "It's dead. I have to move on. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't go around her. Offer to do a 30-minute joint call with the CFO so she's there but you're addressing his concerns directly.",
      commonMistake: "Asking Victoria to refight her internal battle. She's exhausted. You need to take the burden off her.",
      winningMove: "Say: 'Victoria, you've done the work. Let me take the next 30 minutes. Can you set up a call where I address your CFO's specific concerns directly, with you in the room? You don't have to fight — just witness.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 37. MOHAMMED AL-RASHID — The Three Pieces of Paper
  // ═════════════════════════════════════════════════════════════════
  'mohammed-al-rashid-finance-director': {
    id: 'mohammed-al-rashid-finance-director',
    name: 'Mohammed Al-Rashid',
    role: 'Finance Director',
    company: 'Gulfstream Aviation',
    industry: 'aviation',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/mohammed-al-rashid-finance-director.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Three Bids Required',
    scenarioDesc: "Mohammed is 47, Saudi, and his procurement policy requires 3 competing bids for any purchase over $50K. You're one of three. The other two are commoditized vendors selling on price alone. If the process becomes pure price comparison, you lose.",
    goal: 'Reshape the comparison from price to total cost of ownership.',
    category: 'objection-defense',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Policy requires three bids. You're one of three. Your competitors quoted $40K and $45K. You're at $70K. Justify the gap or you lose.",
      impatient1: "I have to defend my choice to my board. Price gap of 70% requires extraordinary justification.",
      impatient2: "Don't tell me you're 'better.' Quantify the gap.",
      hangup: "Going with the cheapest. *click*"
    },
    coachingNotes: {
      whatWorks: "Provide a TCO calculation that includes maintenance, downtime, training, and missed-feature opportunity cost over 3 years. Cheaper upfront often costs more long-term.",
      commonMistake: "Lowering your price to compete with the commodity bids. You'll set a precedent and devalue yourself.",
      winningMove: "Say: 'Mohammed, comparing year-one price is misleading. Over 3 years, including training, downtime, and the features they don't include, our TCO is actually 18% LOWER. Can I send the model?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 38. JENNIFER WALSH — The Champion Just Left
  // ═════════════════════════════════════════════════════════════════
  'jennifer-walsh-cio': {
    id: 'jennifer-walsh-cio',
    name: 'Jennifer Walsh',
    role: 'CIO',
    company: 'Cornerstone Banking',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/jennifer-walsh-cio.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The New Sheriff',
    scenarioDesc: "Jennifer is 44, just joined as CIO three weeks ago. Her predecessor signed your contract. She's reviewing every vendor. New CIOs typically kill 30% of inherited contracts to put their stamp on the org. You're at risk of being one of them.",
    goal: 'Re-sell yourself to a new buyer who never bought you.',
    category: 'objection-defense',
    personalityType: 'skeptical',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I didn't sign your contract. My predecessor did, and I'm reviewing every vendor relationship he inherited me with. Make your case.",
      impatient1: "Don't tell me about my predecessor's reasons. I need to hear yours.",
      impatient2: "Half the vendors I've reviewed got cut. Why should you be different?",
      hangup: "I've heard enough. Reviewing my options. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't lean on the previous relationship. Lead with current usage data, ROI to date, and one specific outcome that aligns with HER priorities, not her predecessor's.",
      commonMistake: "Trying to defend the original sale. She doesn't care. She cares about what you do for HER agenda going forward.",
      winningMove: "Say: 'Jennifer, fair point. Here's what's true today: your team has saved $340K with our tool over the last 18 months. The question isn't why your predecessor bought us — it's whether YOUR roadmap benefits from continuing. Which initiatives of yours are we currently supporting?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 39. RAUL VEGA — The Public Sector RFP
  // ═════════════════════════════════════════════════════════════════
  'raul-vega-director-of-procurement': {
    id: 'raul-vega-director-of-procurement',
    name: 'Raul Vega',
    role: 'Director of Procurement',
    company: 'Pacific Coast School District',
    industry: 'education',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/objection-defense/raul-vega-director-of-procurement.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Public Sector Constraint',
    scenarioDesc: "Raul is 52, runs procurement for a 100,000-student school district. Public sector rules tie his hands. Lowest bidder usually wins by law. Custom terms are nearly impossible. Multi-year deals require board approval. Most reps quit before they understand the rules.",
    goal: 'Work within his constraints to find a path that lets him pick you.',
    category: 'objection-defense',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 8000, lineKey: 'impatient1' },
        { type: 'warn', delay: 15000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 22000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Public sector. State law requires lowest qualified bidder. Your contract terms are non-negotiable on our side. Board approval needed for anything over 1 year.",
      impatient1: "I can't bend the rules. The state attorney general literally reviews our contracts.",
      impatient2: "Don't waste my time trying to find loopholes.",
      hangup: "Bid the RFP or don't. Your choice. *click*"
    },
    coachingNotes: {
      whatWorks: "Ask about state contract piggybacking, cooperative purchasing agreements (Sourcewell, OMNIA Partners). These bypass many lowest-bidder rules.",
      commonMistake: "Trying to negotiate terms. Public sector reps with no understanding of the rules waste everyone's time.",
      winningMove: "Say: 'Raul, I respect the constraints. Are you part of any cooperative purchasing groups like Sourcewell or TIPS? Those let you piggyback on pre-bid contracts and bypass the lowest-bidder requirement for vetted vendors.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 40. KATIE BENNETT — The Implementation Trauma
  // ═════════════════════════════════════════════════════════════════
  'katie-bennett-coo': {
    id: 'katie-bennett-coo',
    name: 'Katie Bennett',
    role: 'COO',
    company: 'Lighthouse Therapeutics',
    industry: 'pharma',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/objection-defense/katie-bennett-coo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Implementation Phobia',
    scenarioDesc: "Katie is 41, COO at a biotech, and the last enterprise tool they implemented took 14 months and went $500K over budget. The vendor's professional services team disappeared 3 months in. Katie now has implementation PTSD. She'd rather pay 2x for proven white-glove than half-price with a complicated rollout.",
    goal: 'Provide implementation guarantees that match her risk tolerance.',
    category: 'objection-defense',
    personalityType: 'skeptical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Our last implementation was 14 months and $500K over budget. The vendor's pro services team disappeared. I will not go through that again.",
      impatient1: "I need real guarantees. SLAs. Penalty clauses. Not promises.",
      impatient2: "If you can't put your money where your mouth is on implementation, we're done.",
      hangup: "Find a customer who hasn't been burned. *click*"
    },
    coachingNotes: {
      whatWorks: "Offer implementation guarantees with real penalty clauses. If you go over the agreed timeline, you eat the cost. Risk transfer is the only thing that breaks through implementation trauma.",
      commonMistake: "Promising a 'smooth implementation' verbally. Words don't heal her wound. Contract terms do.",
      winningMove: "Say: 'Katie, I'll put it in the contract: implementation in 90 days or we credit you the difference at $1,500/day late. That's our skin in the game. Want to see the SLA?'"
    }
  }

};